import React, { useState, useEffect, useMemo, useCallback } from "react";
import styled, { keyframes, css } from "styled-components";
import {
  ServiceItem,
  gbpServicesApi,
} from "../../../services/gbp";
import { getGbpServiceErrorMessage } from "../../../services/gbp/servicesApi";
import { Profile } from "../types";
import { buildServiceKeywordSuggestions } from "../profile-content-suggestions";
import { FieldSyncBadge } from "./FieldSyncBadge";
import { Aside, AsideCard, Badge, Botao, BotaoPequeno, Campo, Coluna, EditorGrid, Hint, Lab, Tip } from "./EditorChrome";
import { readGabaritoState, splitKeywords } from "./gabarito-utils";

// ==================== Helpers ====================

const formatCategoryLabel = (raw?: string): string => {
  if (!raw) return "";
  let cleaned = raw.replace(/^categories\//, "").replace(/^gcid:/, "");
  cleaned = cleaned.replace(/[_-]/g, " ");
  return cleaned
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

// ==================== Animations ====================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

// ==================== Styled Components (prancheta PerfilServicos) ====================

const Container = styled.div`
  animation: ${fadeIn} 0.3s ease;
  min-width: 0;
`;

const Titulo = styled.h3`
  margin: 0;
  font-family: var(--font-sans);
  font-size: 22px;
  line-height: 28px;
  font-weight: 500;
  color: #1f1f1f;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FiltroRapido = styled.button<{ $on?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid ${(p) => (p.$on ? "#EAF1FB" : "#E2E6EA")};
  background: ${(p) => (p.$on ? "#EAF1FB" : "#fff")};
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  color: ${(p) => (p.$on ? "#0B4FA8" : "#2B2D31")};
  cursor: pointer;
  white-space: nowrap;
`;

const Tabela = styled.div`
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 16px;
  overflow: hidden;
`;

const CabecalhoDaTabela = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 130px 110px;
  gap: 16px;
  padding: 12px 16px 12px 44px;
  background: #f8fafc;
  border-bottom: 1px solid var(--line);
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 600;
  color: #5f6368;
  @media (max-width: 599.95px) {
    display: none;
  }
`;

const Grupo = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  height: 52px;
  padding: 0 16px;
  width: 100%;
  background: #f8fafc;
  border: 0;
  border-bottom: 1px solid #eef1f4;
  cursor: pointer;
  text-align: left;
  font: inherit;
  .nome {
    font-family: var(--font-sans);
    font-size: 15px;
    font-weight: 600;
    color: #1f1f1f;
  }
  @media (max-width: 599.95px) {
    flex-wrap: wrap;
    height: auto;
    min-height: 52px;
    padding: 8px 12px;
    gap: 8px;
    .nome {
      flex: 1 1 140px;
      min-width: 0;
      overflow-wrap: anywhere;
    }
  }
`;

const Linha = styled.div<{ $editando?: boolean }>`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 130px 110px;
  align-items: center;
  gap: 16px;
  padding: 12px 16px 12px 44px;
  border-bottom: 1px solid #eef1f4;
  transition: background 0.15s;
  background: ${(p) => (p.$editando ? "#F8FAFD" : "#fff")};
  &:hover {
    background: #eaf1fb;
  }
  .nome {
    font-size: 14px;
    font-weight: 500;
    color: #1f1f1f;
  }
  .desc {
    font-size: 12px;
    line-height: 17px;
    color: #5f6368;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  /* Celular: nome na linha de cima; origem e ações embaixo. */
  @media (max-width: 599.95px) {
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 8px 12px;
    padding: 12px 16px;
    & > :first-child {
      grid-column: 1 / -1;
    }
    & > :nth-child(2) {
      justify-self: start;
    }
    .nome {
      overflow-wrap: anywhere;
    }
  }
`;

const Interruptor = styled.button<{ $on: boolean }>`
  width: 36px;
  height: 20px;
  border-radius: 999px;
  background: ${(p) => (p.$on ? "#1264C7" : "#BDC1C6")};
  position: relative;
  display: inline-block;
  border: 0;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  &::after {
    content: "";
    position: absolute;
    top: 2px;
    left: ${(p) => (p.$on ? "18px" : "2px")};
    width: 16px;
    height: 16px;
    border-radius: 999px;
    background: #fff;
    transition: left 0.15s;
  }
  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
`;

const IconeBotao = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 0;
  background: transparent;
  color: #5f6368;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s;
  &:hover {
    background: #f3f6fa;
    color: #1f1f1f;
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
  .material-icons {
    font-size: 20px;
  }
`;

/* Formulário de adicionar/editar (mesmos campos de antes, no padrão de campos do editor). */
const Formulario = styled.div`
  background: #fff;
  border: 1px solid #c2d7f5;
  border-radius: 16px;
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  animation: ${fadeIn} 0.25s ease;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  .full {
    grid-column: 1 / -1;
  }
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.3px;
  color: #3c4043;
  text-transform: uppercase;
`;

const Input = styled.input`
  width: 100%;
  height: 48px;
  padding: 0 14px;
  border: 1px solid #dadce0;
  border-radius: 12px;
  font-size: 15px;
  font-family: inherit;
  color: #1f1f1f;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: #1264c7;
    box-shadow: 0 0 0 1px #1264c7;
  }
`;

const Select = styled.select`
  width: 100%;
  height: 48px;
  padding: 0 40px 0 14px;
  border: 1px solid #dadce0;
  border-radius: 12px;
  font-size: 15px;
  font-family: inherit;
  color: #1f1f1f;
  background: #fff url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='%235F6368'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E") no-repeat right 12px center;
  appearance: none;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: #1264c7;
    box-shadow: 0 0 0 1px #1264c7;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 96px;
  padding: 12px 14px;
  border: 1px solid #dadce0;
  border-radius: 12px;
  font-size: 15px;
  line-height: 22px;
  font-family: inherit;
  color: #1f1f1f;
  box-sizing: border-box;
  resize: vertical;
  &:focus {
    outline: none;
    border-color: #1264c7;
    box-shadow: 0 0 0 1px #1264c7;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 20px;
  color: #5f6368;
  .material-icons {
    font-size: 44px;
    color: #bdc1c6;
    margin-bottom: 12px;
  }
  h4 {
    font-family: var(--font-sans);
    font-size: 15px;
    font-weight: 600;
    color: #3c4043;
    margin: 0 0 6px;
  }
  p {
    margin: 0;
    font-size: 13px;
  }
`;

const LoadingOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  color: #5f6368;
  .spinner {
    width: 36px;
    height: 36px;
    border: 3px solid #e5e7eb;
    border-top-color: #1264c7;
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
  }
  p {
    margin-top: 14px;
    font-size: 13px;
  }
`;

const Toast = styled.div<{ $type: "error" | "success" }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 13px;
  animation: ${fadeIn} 0.25s ease;
  ${({ $type }) =>
    $type === "error"
      ? css`
          background: #fce8e6;
          color: #b3261e;
        `
      : css`
          background: #eaf7ee;
          color: #137333;
        `}
  .material-icons {
    font-size: 18px;
  }
`;

const ConfirmOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(31, 35, 41, 0.28);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
  animation: ${fadeIn} 0.2s ease;
`;

const ConfirmBox = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  width: 420px;
  max-width: 92vw;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 16px 48px rgba(31, 35, 41, 0.2);
  h4 {
    margin: 0;
    font-family: var(--font-sans);
    font-size: 18px;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
    color: #b3261e;
  }
  p {
    margin: 0;
    font-size: 14px;
    line-height: 20px;
    color: #3c4043;
  }
`;

interface ServicesTabProps {
  profile: Profile | null;
}

export const ServicesTab: React.FC<ServicesTabProps> = ({ profile }) => {
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<"all" | "predefinidos" | "personalizados" | "desativados">("all");
  const [busca, setBusca] = useState("");
  const [gruposFechados, setGruposFechados] = useState<string[]>([]);

  // Form state
  const [formMode, setFormMode] = useState<"closed" | "add" | "edit">("closed");
  const [editingIndex, setEditingIndex] = useState<number>(-1);
  const [formData, setFormData] = useState<ServiceItem>({
    offered: true,
    label: "",
    description: "",
    categoryId: "",
  });

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<{
    index: number;
    label: string;
  } | null>(null);

  // ---------- Derivados ----------

  const filteredServices = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return services.filter((item) => {
      if (activeFilter === "predefinidos" && !item.serviceTypeId) return false;
      if (activeFilter === "personalizados" && item.serviceTypeId) return false;
      if (activeFilter === "desativados" && item.offered !== false) return false;
      if (!termo) return true;
      const nome = (item.label || formatCategoryLabel(item.serviceTypeId) || "").toLowerCase();
      return nome.includes(termo) || (item.description || "").toLowerCase().includes(termo);
    });
  }, [services, activeFilter, busca]);

  /** Serviços visíveis agrupados pela categoria (prancheta: grupos com contagem). */
  const grupos = useMemo(() => {
    const mapa = new Map<string, ServiceItem[]>();
    filteredServices.forEach((item) => {
      const chave = item.categoryId || item.serviceTypeId || "sem_categoria";
      mapa.set(chave, [...(mapa.get(chave) || []), item]);
    });
    return Array.from(mapa.entries()).map(([chave, itens]) => ({ chave, itens }));
  }, [filteredServices]);

  const contagem = {
    todos: services.length,
    predefinidos: services.filter((item) => Boolean(item.serviceTypeId)).length,
    personalizados: services.filter((item) => !item.serviceTypeId).length,
    desativados: services.filter((item) => item.offered === false).length,
  };

  const suggestedServices = useMemo(
    () => {
      if (!profile) return [];
      const g = profile.id ? readGabaritoState(profile.id) : null;
      const kws = g
        ? [
            ...splitKeywords(g.postKeywords || ""),
            ...splitKeywords(g.commentKeywords || ""),
          ]
        : [];
      return buildServiceKeywordSuggestions(profile, kws);
    },
    [profile],
  );

  // publicId do perfil (UUID)
  const publicId = profile?.id;

  // ---------- Fetch Services ----------

  const fetchServices = useCallback(async () => {
    if (!publicId) return;

    try {
      setLoading(true);
      setError(null);

      const [servicesData, categoriesData] = await Promise.all([
        gbpServicesApi.listServices(publicId),
        gbpServicesApi.listServiceCategories(publicId),
      ]);

      // Garante que sempre sejam arrays
      const servicesList = Array.isArray(servicesData) ? servicesData : [];
      const categoriesList = Array.isArray(categoriesData) ? categoriesData : [];

      setServices(servicesList);
      setCategories(categoriesList);
    } catch (err) {
      console.error("[ServicesTab.fetchServices]", err);
      // Não interrompe a renderização apenas mostra vazio
      setServices([]);
      setCategories([]);
      // Mostra mensagem apenas se houver erro real
      const message = err instanceof Error ? err.message : String(err);
      if (!message.includes("all attempts failed")) {
        setError("Não foi possível carregar os serviços.");
      }
    } finally {
      setLoading(false);
    }
  }, [publicId]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  // ---------- Form Helpers ----------

  const resetForm = () => {
    setFormData({ offered: true, label: "", description: "", categoryId: "" });
    setFormMode("closed");
    setEditingIndex(-1);
  };

  const openAddForm = () => {
    setFormData({ offered: true, label: "", description: "", categoryId: "" });
    setFormMode("add");
    setEditingIndex(-1);
  };

  const openSuggestedService = (service: ServiceItem) => {
    setFormData(service);
    setFormMode("add");
    setEditingIndex(-1);
  };

  const openEditForm = (index: number) => {
    const service = services[index];
    setFormData({ ...service });
    setFormMode("edit");
    setEditingIndex(index);
  };

  // ---------- Stable getters ----------

  // ---------- CRUD Handlers ----------

  /** Adicionar novo serviço via POST */
  const handleAddService = async () => {
    if (!publicId || !formData.label) return;

    try {
      setSaving(true);
      setError(null);

      const result = await gbpServicesApi.createService(publicId, formData);
      setServices(result);
      resetForm();
      setSuccess("Serviço adicionado com sucesso!");
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      setError(getGbpServiceErrorMessage(error, "Erro ao adicionar serviço."));
      setTimeout(() => setError(null), 10000);
    } finally {
      setSaving(false);
    }
  };

  /** Editar serviço existente via PUT (envia lista completa atualizada) */
  const handleEditService = async () => {
    if (!publicId || editingIndex < 0 || !formData.label) return;

    const updatedList = services.map((s, i) =>
      i === editingIndex ? { ...formData, offered: true } : s
    );

    try {
      setSaving(true);
      setError(null);

      const result = await gbpServicesApi.updateServices(publicId, updatedList);
      setServices(result);
      resetForm();
      setSuccess("Serviço atualizado com sucesso!");
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      setError(getGbpServiceErrorMessage(error, "Erro ao atualizar serviço."));
      setTimeout(() => setError(null), 10000);
    } finally {
      setSaving(false);
    }
  };

  /** Excluir serviço via PUT (envia lista sem o item removido) */
  const handleDeleteService = async (index: number) => {
    if (!publicId) return;

    const updatedList = services.filter((_, i) => i !== index);

    try {
      setSaving(true);
      setError(null);

      const result = await gbpServicesApi.updateServices(publicId, updatedList);
      setServices(result);
      setDeleteTarget(null);
      setSuccess("Serviço excluído com sucesso!");
      setTimeout(() => setSuccess(null), 3000);

      // Se estava editando esse serviço, fechar o form
      if (editingIndex === index) resetForm();
    } catch (error) {
      setError(getGbpServiceErrorMessage(error, "Erro ao excluir serviço."));
      setTimeout(() => setError(null), 10000);
    } finally {
      setSaving(false);
    }
  };

  /** Liga/desliga o serviço na ficha (campo `offered`) via PUT com a lista completa. */
  const handleToggleOffered = async (index: number) => {
    if (!publicId) return;
    const updatedList = services.map((item, i) => (i === index ? { ...item, offered: item.offered === false } : item));
    try {
      setSaving(true);
      setError(null);
      const result = await gbpServicesApi.updateServices(publicId, updatedList);
      setServices(result);
      setSuccess(updatedList[index].offered ? "Serviço ativado na ficha." : "Serviço desativado na ficha.");
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      setError(getGbpServiceErrorMessage(error, "Erro ao alterar o serviço."));
      setTimeout(() => setError(null), 10000);
    } finally {
      setSaving(false);
    }
  };

  const handleFormSubmit = () => {
    if (formMode === "add") handleAddService();
    else if (formMode === "edit") handleEditService();
  };

  // ---------- Renders ----------

  if (loading && !services.length) {
    return (
      <LoadingOverlay>
        <div className="spinner" />
        <p>Carregando serviços...</p>
      </LoadingOverlay>
    );
  }

  if (!publicId) {
    return (
      <Container>
        <Toast $type="error">
          <span className="material-icons">error_outline</span>
          <span>{error || "Perfil não vinculado ao Google Business Profile."}</span>
        </Toast>
      </Container>
    );
  }

  const ativos = services.filter((item) => item.offered !== false).length;
  const nomeDoServico = (item: ServiceItem) => item.label || formatCategoryLabel(item.serviceTypeId) || "Serviço sem nome";

  return (
    <Container>
      <EditorGrid style={{ gridTemplateColumns: "minmax(0, 8fr) minmax(0, 4fr)" }}>
        <Coluna style={{ gap: 20 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <Titulo>
                Serviços
                <FieldSyncBadge kind="backend" />
              </Titulo>
              <Hint>
                {`Lista que aparece na aba "Serviços" da ficha, agrupada por categoria. ${ativos} ${ativos === 1 ? "ativo" : "ativos"} · ${contagem.desativados} ${contagem.desativados === 1 ? "desativado" : "desativados"}.`}
              </Hint>
            </div>
            <Botao type="button" $variante={formMode === "add" ? "outlined" : "filled"} onClick={() => (formMode !== "closed" ? resetForm() : openAddForm())}>
              <span className="material-icons">{formMode !== "closed" ? "close" : "add"}</span>
              {formMode !== "closed" ? "Fechar" : "Adicionar serviço"}
            </Botao>
          </div>

          {error && (
            <Toast $type="error" role="alert">
              <span className="material-icons">error_outline</span>
              <span>{error}</span>
            </Toast>
          )}
          {success && (
            <Toast $type="success" role="status">
              <span className="material-icons">check_circle</span>
              <span>{success}</span>
            </Toast>
          )}

          {formMode === "add" && (
            <Formulario>
                  <Titulo style={{ fontSize: 18 }}>
                    <span className="material-icons" style={{ color: "#1264C7" }}>add_circle_outline</span>
                    Novo serviço
                  </Titulo>
                  <FormGrid>
                    <FormGroup>
                      <Label htmlFor="servico-nome">
                        Nome do serviço *
                        <FieldSyncBadge kind="backend" />
                      </Label>
                      <Input
                        id="servico-nome"
                        type="text"
                        value={formData.label || ""}
                        onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                        placeholder="Ex: Corte de cabelo"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label htmlFor="servico-categoria">
                        Categoria
                        <FieldSyncBadge kind="backend" />
                      </Label>
                      <Select id="servico-categoria" value={formData.categoryId || ""} onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}>
                        <option value="">Selecione uma categoria</option>
                        {categories.map((cat) => (
                          <option key={cat} value={cat}>
                            {formatCategoryLabel(cat)}
                          </option>
                        ))}
                        <option value="custom_service">Serviço personalizado</option>
                      </Select>
                    </FormGroup>
                    <FormGroup className="full">
                      <Label htmlFor="servico-descricao">
                        Descrição
                        <FieldSyncBadge kind="backend" />
                      </Label>
                      <TextArea
                        id="servico-descricao"
                        value={formData.description || ""}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Descreva brevemente o serviço..."
                      />
                    </FormGroup>
                  </FormGrid>
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                    <BotaoPequeno type="button" $variante="text" onClick={resetForm}>
                      Cancelar
                    </BotaoPequeno>
                    <BotaoPequeno type="button" $variante="filled" onClick={handleFormSubmit} disabled={saving || !formData.label}>
                      <span className="material-icons">add</span>
                      {saving ? "Salvando..." : "Adicionar"}
                    </BotaoPequeno>
                  </div>
                </Formulario>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <Campo style={{ width: 320, maxWidth: "100%", height: 44 }}>
              <span className="material-icons">search</span>
              <input type="search" placeholder="Buscar serviço" aria-label="Buscar serviço" value={busca} onChange={(e) => setBusca(e.target.value)} />
            </Campo>
            {[
              { chave: "all" as const, rotulo: `Todos · ${contagem.todos}` },
              { chave: "predefinidos" as const, rotulo: `Predefinidos · ${contagem.predefinidos}` },
              { chave: "personalizados" as const, rotulo: `Personalizados · ${contagem.personalizados}` },
              { chave: "desativados" as const, rotulo: `Desativados · ${contagem.desativados}` },
            ].map((filtro) => (
              <FiltroRapido key={filtro.chave} type="button" $on={activeFilter === filtro.chave} aria-pressed={activeFilter === filtro.chave} onClick={() => setActiveFilter(filtro.chave)}>
                {filtro.rotulo}
              </FiltroRapido>
            ))}
          </div>

          <Tabela>
            <CabecalhoDaTabela>
              <span>Serviço</span>
              <span>Origem</span>
              <span>Ativo</span>
            </CabecalhoDaTabela>
            {filteredServices.length === 0 && (
              <EmptyState>
                <span className="material-icons">handyman</span>
                <h4>Nenhum serviço encontrado</h4>
                <p>{services.length === 0 ? "Adicione os serviços que sua empresa oferece." : "Nenhum serviço com esse filtro."}</p>
              </EmptyState>
            )}
            {grupos.map((grupo) => {
              const fechado = gruposFechados.includes(grupo.chave);
              return (
                <div key={grupo.chave}>
                  <Grupo type="button" aria-expanded={!fechado} onClick={() => setGruposFechados((atual) => (fechado ? atual.filter((c) => c !== grupo.chave) : [...atual, grupo.chave]))}>
                    <span className="material-icons" style={{ color: "#5F6368" }}>{fechado ? "chevron_right" : "expand_more"}</span>
                    <span className="nome">{grupo.chave === "sem_categoria" ? "Sem categoria" : formatCategoryLabel(grupo.chave)}</span>
                    <Badge $tom="cinza">{`${grupo.itens.length} ${grupo.itens.length === 1 ? "serviço" : "serviços"}`}</Badge>
                    <span style={{ flex: 1 }} />
                    <BotaoPequeno
                      as="span"
                      role="button"
                      tabIndex={0}
                      $variante="text"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        openAddForm();
                        setFormData({ offered: true, label: "", description: "", categoryId: grupo.chave === "sem_categoria" ? "" : grupo.chave });
                      }}
                      onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          openAddForm();
                          setFormData({ offered: true, label: "", description: "", categoryId: grupo.chave === "sem_categoria" ? "" : grupo.chave });
                        }
                      }}
                    >
                      <span className="material-icons">add</span>
                      Adicionar nesta categoria
                    </BotaoPequeno>
                  </Grupo>
                  {!fechado &&
                    grupo.itens.map((service) => {
                      const realIndex = services.indexOf(service);
                      const editando = formMode === "edit" && editingIndex === realIndex;
                      const ativo = service.offered !== false;
                      return (
                        <React.Fragment key={`svc-${realIndex}`}>
                          <Linha $editando={editando}>
                            <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                              <span className="nome">{nomeDoServico(service)}</span>
                              {service.description && <span className="desc" title={service.description}>{service.description}</span>}
                            </div>
                            <Badge $tom={service.serviceTypeId ? "verde" : "azul"}>{service.serviceTypeId ? "Predefinido" : "Personalizado"}</Badge>
                            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                              <Interruptor
                                type="button"
                                role="switch"
                                $on={ativo}
                                aria-checked={ativo}
                                aria-label={ativo ? `Desativar ${nomeDoServico(service)}` : `Ativar ${nomeDoServico(service)}`}
                                disabled={saving}
                                onClick={() => handleToggleOffered(realIndex)}
                              />
                              <IconeBotao type="button" title="Editar serviço" aria-label={`Editar ${nomeDoServico(service)}`} onClick={() => openEditForm(realIndex)} disabled={saving}>
                                <span className="material-icons">edit</span>
                              </IconeBotao>
                              <IconeBotao type="button" title="Excluir serviço" aria-label={`Excluir ${nomeDoServico(service)}`} onClick={() => setDeleteTarget({ index: realIndex, label: nomeDoServico(service) })} disabled={saving}>
                                <span className="material-icons">delete</span>
                              </IconeBotao>
                            </div>
                          </Linha>
                          {editando && (
                            <div style={{ padding: "12px 16px 16px 44px", borderBottom: "1px solid #EEF1F4", background: "#F8FAFD" }}>
            <Formulario>
                                <Titulo style={{ fontSize: 18 }}>
                                  <span className="material-icons" style={{ color: "#1264C7" }}>{formMode === "edit" ? "edit" : "add_circle_outline"}</span>
                                  {formMode === "edit" ? "Editar serviço" : "Novo serviço"}
                                </Titulo>
                                <FormGrid>
                                  <FormGroup>
                                    <Label htmlFor="servico-nome">
                                      Nome do serviço *
                                      <FieldSyncBadge kind="backend" />
                                    </Label>
                                    <Input
                                      id="servico-nome"
                                      type="text"
                                      value={formData.label || ""}
                                      onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                                      placeholder="Ex: Corte de cabelo"
                                    />
                                  </FormGroup>
                                  <FormGroup>
                                    <Label htmlFor="servico-categoria">
                                      Categoria
                                      <FieldSyncBadge kind="backend" />
                                    </Label>
                                    <Select id="servico-categoria" value={formData.categoryId || ""} onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}>
                                      <option value="">Selecione uma categoria</option>
                                      {categories.map((cat) => (
                                        <option key={cat} value={cat}>
                                          {formatCategoryLabel(cat)}
                                        </option>
                                      ))}
                                      <option value="custom_service">Serviço personalizado</option>
                                    </Select>
                                  </FormGroup>
                                  <FormGroup className="full">
                                    <Label htmlFor="servico-descricao">
                                      Descrição
                                      <FieldSyncBadge kind="backend" />
                                    </Label>
                                    <TextArea
                                      id="servico-descricao"
                                      value={formData.description || ""}
                                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                      placeholder="Descreva brevemente o serviço..."
                                    />
                                  </FormGroup>
                                </FormGrid>
                                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                                  <BotaoPequeno type="button" $variante="text" onClick={resetForm}>
                                    Cancelar
                                  </BotaoPequeno>
                                  <BotaoPequeno type="button" $variante="filled" onClick={handleFormSubmit} disabled={saving || !formData.label}>
                                    <span className="material-icons">{formMode === "edit" ? "save" : "add"}</span>
                                    {saving ? "Salvando..." : formMode === "edit" ? "Salvar serviço" : "Adicionar"}
                                  </BotaoPequeno>
                                </div>
                              </Formulario>
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                </div>
              );
            })}
          </Tabela>
        </Coluna>

        <Aside>
          <AsideCard>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Lab>
                <span className="material-icons" style={{ color: "#1264C7" }}>auto_awesome</span>
                Sugestões GeoRanking
              </Lab>
              <Badge>{suggestedServices.length}</Badge>
            </div>
            <Hint>
              {profile?.category
                ? `Serviços com intenção de compra para "${profile.category}", montados a partir das palavras do Gabarito, que ainda não estão na ficha.`
                : "Serviços com intenção de compra montados a partir das palavras do Gabarito."}
            </Hint>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {suggestedServices.slice(0, 6).map((service) => (
                <div key={service.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "8px 10px", borderRadius: 10, background: "#F7F9FC" }}>
                  <span style={{ fontSize: 13, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{service.label}</span>
                  <BotaoPequeno type="button" $variante="tonal" style={{ height: 30 }} onClick={() => openSuggestedService(service)}>
                    Adicionar
                  </BotaoPequeno>
                </div>
              ))}
              {suggestedServices.length === 0 && <Hint>Preencha as palavras do Gabarito para receber sugestões.</Hint>}
            </div>
          </AsideCard>
          <Tip>
            <span className="material-icons">info</span>
            Ao remover uma categoria na aba Informações básicas, os serviços dela são apagados pelo Google e não voltam sozinhos.
          </Tip>
        </Aside>
      </EditorGrid>

      {deleteTarget && (
        <ConfirmOverlay onClick={() => !saving && setDeleteTarget(null)}>
          <ConfirmBox role="dialog" aria-label="Excluir serviço" onClick={(e) => e.stopPropagation()}>
            <h4>
              <span className="material-icons">warning</span>
              Excluir serviço
            </h4>
            <p>
              Tem certeza que deseja excluir o serviço <b>"{deleteTarget.label}"</b>? Esta ação não pode ser desfeita.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 6 }}>
              <BotaoPequeno type="button" $variante="text" onClick={() => setDeleteTarget(null)} disabled={saving}>
                Cancelar
              </BotaoPequeno>
              <BotaoPequeno type="button" $variante="outlined" style={{ color: "#B3261E", borderColor: "#F9DEDC" }} onClick={() => handleDeleteService(deleteTarget.index)} disabled={saving}>
                <span className="material-icons">delete</span>
                {saving ? "Excluindo..." : "Excluir"}
              </BotaoPequeno>
            </div>
          </ConfirmBox>
        </ConfirmOverlay>
      )}
    </Container>
  );
};

export default ServicesTab;
